/**
 * 待确认买入/赎回：自动转正、在途汇总、状态文案
 */
import {
  availableRedeemShares,
  EPS,
  isPendingBuyTrade,
  isPendingRedeemTrade
} from './assetHoldingCalc';
import { getHoldings, productKey, saveHoldings } from './assetHoldingStore';
import { ensureAutoExpectedRedeemOnTrade, getTermDaysForProduct } from './assetRedeemEligibility';
import { readTrendNav } from './assetTrendNavCache';
import { DEFAULT_TREND_LIMIT, fetchProductTrendNav, trendNavToHistoryRows } from './assetTrendFetch';
import { todayYMD, fmtLocalYMD, parseLocalYMD } from './assetTradingCalendar';
import * as tradeNavDate from './assetTradeNavDate';

export const NAV_RETRY_GRACE_DAYS = 8;

const AUTO_CONFIRM_MODES = new Set(['coll_establish', 't_plus_1', 't_plus_2']);

function formatMoney2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '--';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const s = abs.toFixed(2);
  const parts = s.split('.');
  const intPart = parts[0] || '0';
  const decPart = parts[1] || '00';
  const intWithComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${intWithComma}.${decPart}`;
}

function formatShares2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '--';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const s = abs.toFixed(2);
  const parts = s.split('.');
  const intPart = parts[0] || '0';
  const decPart = parts[1] || '00';
  const intWithComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${intWithComma}.${decPart}`;
}

function normYmd(x) {
  return tradeNavDate.normYmd(x);
}

function addNaturalDays(ymd, delta) {
  const d = normYmd(ymd);
  if (!d) return '';
  const n = Number(delta);
  if (!Number.isFinite(n)) return d;
  const dt = parseLocalYMD(d);
  dt.setDate(dt.getDate() + n);
  return fmtLocalYMD(dt);
}

function round6(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 1e6) / 1e6;
}

function floor2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.floor(v * 100 + 1e-9) / 100;
}

function getTradeMeta(holding, productMetaMap) {
  const h = holding && typeof holding === 'object' ? holding : {};
  const pid = h.productId != null ? String(h.productId) : '';
  const m = (productMetaMap && pid)
    ? (productMetaMap[pid] || productMetaMap[Number(pid)] || productMetaMap[h.productId] || {})
    : {};
  const confirmTime = m.confirmTime != null ? String(m.confirmTime).trim() : '';
  return {
    confirmTime,
    isTPlus2: tradeNavDate.isTPlus2ConfirmTime(confirmTime),
    establishDateYmd: m.establishDateYmd ? String(m.establishDateYmd).slice(0, 10) : '',
    collStartYmd: m.collStartYmd ? String(m.collStartYmd).slice(0, 10) : '',
    collEndYmd: m.collEndYmd ? String(m.collEndYmd).slice(0, 10) : ''
  };
}

function pendingAnchorYmd(trade) {
  if (isPendingBuyTrade(trade)) return normYmd(trade && trade.paymentDate);
  if (isPendingRedeemTrade(trade)) return normYmd(trade && trade.submitDate);
  return '';
}

export function buildPredictOpts(holding, trade, productMetaMap) {
  const meta = getTradeMeta(holding, productMetaMap);
  const t = trade && typeof trade === 'object' ? trade : {};
  // 录入表单草稿常只带 paymentDate/submitDate、未带 type。
  // 无 type 时 isRedeemTrade 为 true，pendingAnchorYmd 会去读 submitDate → 待确认买入推算失败。
  let paymentYmd = pendingAnchorYmd(t);
  if (!paymentYmd) {
    paymentYmd = normYmd(t.paymentDate) || normYmd(t.submitDate) || normYmd(t.paymentYmd);
  }
  return {
    paymentYmd,
    confirmTime: meta.confirmTime,
    establishYmd: meta.establishDateYmd,
    collStartYmd: meta.collStartYmd,
    collEndYmd: meta.collEndYmd
  };
}

function resolvePendingReason(trade, today) {
  const t = today ? String(today).slice(0, 10) : todayYMD();
  const expected = normYmd(trade && trade.expectedConfirmYmd);
  const mode = trade && trade.confirmMode ? String(trade.confirmMode) : '';

  // 可赎份额不足：始终需手动
  if (trade && trade.confirmPendingReason === 'shares_insufficient') return 'need_manual';
  // 超过预计确认日 + 宽限自然日
  if (expected && t > addNaturalDays(expected, NAV_RETRY_GRACE_DAYS)) return 'need_manual';
  // manual 且没有任何预计确认日 → 无法自动；有预计确认日则仍按日期/净值走
  if (mode === 'manual' && !expected) return 'need_manual';
  if (!expected || t < expected) return 'await_date';
  return 'await_nav';
}

/** 本地产品缓存暂时 miss 时，保留已识别的自动确认模式，避免被写成 manual */
function resolveConfirmMode(predictedMode, prevMode) {
  const pred = predictedMode != null ? String(predictedMode) : '';
  const prev = prevMode != null ? String(prevMode) : '';
  if (AUTO_CONFIRM_MODES.has(pred)) return pred;
  if (pred === 'manual' && AUTO_CONFIRM_MODES.has(prev)) return prev;
  return pred || prev || 'manual';
}

export function buildPendingStatusText(trade, today) {
  const reason = resolvePendingReason(trade, today);
  const expected = normYmd(trade && trade.expectedConfirmYmd);
  if (reason === 'need_manual') return '需手动确认';
  if (reason === 'await_date' && expected) {
    const mmdd = expected.length >= 10 ? expected.slice(5, 10) : expected;
    return `预计 ${mmdd} 确认`;
  }
  return '确认中，待净值';
}

function readNavLocal(productId, navDate) {
  const pid = Number(productId);
  const d = normYmd(navDate);
  if (!(pid > 0) || !d) return null;
  const t = readTrendNav(pid);
  const nv = t?.navByDate?.[d] != null ? Number(t.navByDate[d]) : NaN;
  if (Number.isFinite(nv) && nv > 0) return nv;
  return null;
}

async function fetchNavRemote(productId, navDate) {
  const pid = Number(productId);
  const d = normYmd(navDate);
  if (!(pid > 0) || !d) return null;
  try {
    const res = await fetchProductTrendNav(pid, DEFAULT_TREND_LIMIT);
    const rows = trendNavToHistoryRows(res.datesAsc, res.navByDate);
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const date = row?.date ? String(row.date).slice(0, 10) : '';
      if (date !== d) continue;
      const nav = Number(row.unit_nav ?? row.nav ?? row.accumulated_nav);
      if (Number.isFinite(nav) && nav > 0) return nav;
    }
  } catch (error) {
    // ignore
  }
  return null;
}

function applyConfirmedBuyTrade(trade, confirmYmd, confirmNav, navSourceDate) {
  const amount = Number(trade && trade.amount) || 0;
  const nav = Number(confirmNav);
  return {
    ...trade,
    status: 'confirmed',
    confirmDate: normYmd(confirmYmd),
    confirmNav: nav,
    confirmNavSourceDate: normYmd(navSourceDate),
    shares: round6(amount / nav),
    autoConfirmed: true,
    confirmPendingReason: ''
  };
}

function applyConfirmedRedeemTrade(trade, confirmYmd, confirmNav, navSourceDate) {
  const shares = round6(Number(trade && trade.shares));
  const nav = Number(confirmNav);
  return {
    ...trade,
    status: 'confirmed',
    confirmDate: normYmd(confirmYmd),
    confirmNav: nav,
    confirmNavSourceDate: normYmd(navSourceDate),
    shares,
    amount: floor2(shares * nav),
    autoConfirmed: true,
    confirmPendingReason: ''
  };
}

export function enrichPendingTrade(trade, holding, productMetaMap) {
  const anchor = pendingAnchorYmd(trade);
  if (!anchor) return trade;
  const predicted = tradeNavDate.predictConfirmDate(buildPredictOpts(holding, trade, productMetaMap));
  const userLocked = !!(trade && trade.expectedConfirmCustomized);
  const expectedConfirmYmd = userLocked
    ? (normYmd(trade.expectedConfirmYmd) || predicted.expectedConfirmYmd || '')
    : (predicted.expectedConfirmYmd || normYmd(trade.expectedConfirmYmd) || '');
  const confirmMode = resolveConfirmMode(predicted.confirmMode, trade && trade.confirmMode);
  const today = todayYMD();
  let reason = resolvePendingReason({
    ...trade,
    expectedConfirmYmd,
    confirmMode,
    // 重新计算时不沿用旧的 need_manual 粘性（shares_insufficient 仍由下方覆盖）
    confirmPendingReason: trade && trade.confirmPendingReason === 'shares_insufficient'
      ? 'shares_insufficient'
      : ''
  }, today);
  if (isPendingRedeemTrade(trade)) {
    const avail = availableRedeemShares(holding && holding.trades, trade.tradeId);
    const need = Number(trade.shares) || 0;
    if (need > avail + EPS) reason = 'shares_insufficient';
  }
  return {
    ...trade,
    expectedConfirmYmd,
    confirmMode,
    confirmPendingReason: reason
  };
}

function isPendingTrade(t) {
  return isPendingBuyTrade(t) || isPendingRedeemTrade(t);
}

async function processOnePendingTrade(t, h, trades, ti, opts) {
  const allowRemote = !!(opts && opts.allowRemote);
  const productMetaMap = opts?.productMetaMap || {};
  const today = todayYMD();
  const pid = h.productId != null ? Number(h.productId) : 0;

  const enriched = enrichPendingTrade(t, h, productMetaMap);
  // 仅真正 need_manual 时跳过；manual 但已有预计确认日时仍尝试取净值转正
  if (enriched.confirmPendingReason === 'need_manual') {
    if (t.confirmPendingReason !== enriched.confirmPendingReason
      || t.expectedConfirmYmd !== enriched.expectedConfirmYmd
      || t.confirmMode !== enriched.confirmMode) {
      trades[ti] = enriched;
      return { dirty: true, confirmed: false };
    }
    return { dirty: false, confirmed: false };
  }

  const expected = normYmd(enriched.expectedConfirmYmd);
  if (!expected || today < expected) {
    if (t.confirmPendingReason !== enriched.confirmPendingReason
      || t.expectedConfirmYmd !== enriched.expectedConfirmYmd
      || t.confirmMode !== enriched.confirmMode) {
      trades[ti] = enriched;
      return { dirty: true, confirmed: false };
    }
    return { dirty: false, confirmed: false };
  }

  if (isPendingRedeemTrade(enriched)) {
    const avail = availableRedeemShares(trades, enriched.tradeId);
    const need = Number(enriched.shares) || 0;
    if (need > avail + EPS) {
      enriched.confirmPendingReason = 'shares_insufficient';
      trades[ti] = enriched;
      return { dirty: true, confirmed: false };
    }
  }

  const predicted = tradeNavDate.predictConfirmDate(buildPredictOpts(h, enriched, productMetaMap));
  const navSourceYmd = normYmd(predicted.confirmNavSourceYmd) || expected;
  let nav = readNavLocal(pid, navSourceYmd);
  if (nav == null && allowRemote) {
    nav = await fetchNavRemote(pid, navSourceYmd);
  }
  if (nav == null) {
    enriched.confirmPendingReason = 'await_nav';
    // 已是「确认中，待净值」且字段未变时不要标 dirty，避免无意义写回覆盖用户删除
    if (t.confirmPendingReason !== enriched.confirmPendingReason
      || t.expectedConfirmYmd !== enriched.expectedConfirmYmd
      || t.confirmMode !== enriched.confirmMode) {
      trades[ti] = enriched;
      return { dirty: true, confirmed: false };
    }
    return { dirty: false, confirmed: false };
  }

  if (isPendingRedeemTrade(enriched)) {
    trades[ti] = applyConfirmedRedeemTrade(enriched, expected, nav, navSourceYmd);
  } else {
    const mode = enriched.confirmMode || predicted.confirmMode || '';
    const confirmNav = mode === 'coll_establish'
      ? tradeNavDate.ESTABLISH_SUBSCRIBE_CONFIRM_NAV
      : nav;
    const confirmed = applyConfirmedBuyTrade(enriched, expected, confirmNav, navSourceYmd);
    const meta = productMetaMap[h.productId] || productMetaMap[String(pid)] || {};
    const termDays = getTermDaysForProduct(meta);
    trades[ti] = ensureAutoExpectedRedeemOnTrade(confirmed, termDays);
  }
  return { dirty: true, confirmed: true };
}

/**
 * 把自动确认结果合并进「当前」localStorage，避免 await 期间用户删除后被旧快照写回复活。
 */
function mergeAutoConfirmIntoCurrent(processedList) {
  const current = getHoldings();
  if (!Array.isArray(current) || !current.length) return current;
  const processedByHolding = new Map(
    (processedList || []).map((h) => [String(h.holdingId), h])
  );
  return current.map((h) => {
    const processed = processedByHolding.get(String(h.holdingId));
    if (!processed) return h;
    const procByTradeId = new Map(
      (processed.trades || []).map((t) => [String(t.tradeId || ''), t])
    );
    const trades = (h.trades || []).map((t) => {
      const id = String(t.tradeId || '');
      if (!id) return t;
      return procByTradeId.get(id) || t;
    });
    return { ...h, trades, updatedAt: Date.now() };
  });
}

export async function tryAutoConfirmPendingTrades(holdings, opts = {}) {
  const list = Array.isArray(holdings) ? holdings.slice() : [];
  let changed = false;
  let confirmedCount = 0;

  for (let hi = 0; hi < list.length; hi += 1) {
    const h = list[hi];
    const trades = Array.isArray(h.trades) ? h.trades.slice() : [];
    let holdingDirty = false;

    for (let ti = 0; ti < trades.length; ti += 1) {
      const t = trades[ti];
      if (!isPendingTrade(t)) continue;
      // eslint-disable-next-line no-await-in-loop
      const res = await processOnePendingTrade(t, h, trades, ti, opts);
      if (res.dirty) holdingDirty = true;
      if (res.confirmed) confirmedCount += 1;
    }

    if (holdingDirty) {
      list[hi] = { ...h, trades };
      changed = true;
    }
  }

  if (changed) {
    const merged = mergeAutoConfirmIntoCurrent(list);
    saveHoldings(merged);
    return { changed, confirmedCount, holdings: merged };
  }
  return { changed, confirmedCount, holdings: list };
}

function listPendingBuyTrades(holdings, agencyName, productMetaMap) {
  const items = [];
  (holdings || []).forEach((h) => {
    if (!h) return;
    const agency = h.agencyName != null ? String(h.agencyName).trim() : '';
    if (agencyName && agencyName !== '全部机构' && agency !== agencyName) return;
    (h.trades || []).forEach((t) => {
      if (!isPendingBuyTrade(t)) return;
      const enriched = enrichPendingTrade(t, h, productMetaMap);
      const amount = Number(enriched.amount) || 0;
      items.push({
        tradeId: enriched.tradeId || '',
        holdingId: h.holdingId ? String(h.holdingId) : '',
        productId: h.productId != null ? Number(h.productId) : 0,
        productCategory: h.productCategory != null ? String(h.productCategory) : '',
        productName: h.productName ? String(h.productName) : '未知产品',
        agencyName: agency,
        paymentDate: normYmd(enriched.paymentDate),
        amount,
        amountText: formatMoney2(amount),
        expectedConfirmYmd: normYmd(enriched.expectedConfirmYmd),
        statusText: buildPendingStatusText(enriched),
        confirmMode: enriched.confirmMode || ''
      });
    });
  });
  items.sort((a, b) => {
    const da = a.paymentDate || '';
    const db = b.paymentDate || '';
    if (da !== db) return db.localeCompare(da);
    return String(b.tradeId).localeCompare(String(a.tradeId));
  });
  return items;
}

function listPendingRedeemTrades(holdings, agencyName, productMetaMap) {
  const items = [];
  (holdings || []).forEach((h) => {
    if (!h) return;
    const agency = h.agencyName != null ? String(h.agencyName).trim() : '';
    if (agencyName && agencyName !== '全部机构' && agency !== agencyName) return;
    (h.trades || []).forEach((t) => {
      if (!isPendingRedeemTrade(t)) return;
      const enriched = enrichPendingTrade(t, h, productMetaMap);
      const shares = Number(enriched.shares) || 0;
      items.push({
        tradeId: enriched.tradeId || '',
        holdingId: h.holdingId ? String(h.holdingId) : '',
        productId: h.productId != null ? Number(h.productId) : 0,
        productCategory: h.productCategory != null ? String(h.productCategory) : '',
        productName: h.productName ? String(h.productName) : '未知产品',
        agencyName: agency,
        submitDate: normYmd(enriched.submitDate),
        shares,
        sharesText: formatShares2(shares),
        expectedConfirmYmd: normYmd(enriched.expectedConfirmYmd),
        statusText: buildPendingStatusText(enriched),
        confirmMode: enriched.confirmMode || ''
      });
    });
  });
  items.sort((a, b) => {
    const da = a.submitDate || '';
    const db = b.submitDate || '';
    if (da !== db) return db.localeCompare(da);
    return String(b.tradeId).localeCompare(String(a.tradeId));
  });
  return items;
}

export function computePendingBuySummary(holdings, agencyName, productMetaMap = {}) {
  const items = listPendingBuyTrades(holdings, agencyName, productMetaMap);
  let totalAmount = 0;
  items.forEach((it) => {
    if (Number.isFinite(it.amount)) totalAmount += it.amount;
  });
  return {
    show: items.length > 0,
    count: items.length,
    totalAmount,
    totalText: formatMoney2(totalAmount),
    items
  };
}

export function computePendingRedeemSummary(holdings, agencyName, productMetaMap = {}) {
  const items = listPendingRedeemTrades(holdings, agencyName, productMetaMap);
  let totalShares = 0;
  items.forEach((it) => {
    if (Number.isFinite(it.shares)) totalShares += it.shares;
  });
  return {
    show: items.length > 0,
    count: items.length,
    totalShares,
    totalSharesText: formatShares2(totalShares),
    items
  };
}

export function buildPendingTransitSummaryText(buyCount, redeemCount) {
  const parts = [];
  if (buyCount > 0) parts.push(`买入 ${buyCount} 笔`);
  if (redeemCount > 0) parts.push(`赎回 ${redeemCount} 笔`);
  return parts.length ? parts.join(' · ') : '';
}

export function productIdsNeedingPendingNav(holdings, productMetaMap = {}) {
  const today = todayYMD();
  const pids = new Set();
  (holdings || []).forEach((h) => {
    const pid = h && h.productId != null ? Number(h.productId) : 0;
    if (!(pid > 0)) return;
    (h.trades || []).forEach((t) => {
      if (!isPendingTrade(t)) return;
      const enriched = enrichPendingTrade(t, h, productMetaMap);
      if (enriched.confirmPendingReason === 'need_manual') return;
      if (enriched.confirmPendingReason === 'shares_insufficient') return;
      const expected = normYmd(enriched.expectedConfirmYmd);
      if (!expected || today < expected) return;
      const predicted = tradeNavDate.predictConfirmDate(buildPredictOpts(h, enriched, productMetaMap));
      const navSourceYmd = normYmd(predicted.confirmNavSourceYmd) || expected;
      const nav = readNavLocal(pid, navSourceYmd);
      if (nav == null) pids.add(pid);
    });
  });
  return Array.from(pids);
}

/**
 * 是否存在「预计确认日≤今天、仍可自动转正」的在途（含确认中待净值）。
 * 不看本地是否已有净值；供资产中心进页独立重试（走本机 /api），与进度拉数门闩脱钩。
 */
export function hasPendingDueForConfirm(holdings, productMetaMap = {}) {
  const today = todayYMD();
  const arr = Array.isArray(holdings) ? holdings : [];
  for (let hi = 0; hi < arr.length; hi += 1) {
    const h = arr[hi];
    const trades = h && Array.isArray(h.trades) ? h.trades : [];
    for (let ti = 0; ti < trades.length; ti += 1) {
      const t = trades[ti];
      if (!isPendingTrade(t)) continue;
      const enriched = enrichPendingTrade(t, h, productMetaMap);
      if (enriched.confirmPendingReason === 'need_manual') continue;
      if (enriched.confirmPendingReason === 'shares_insufficient') continue;
      const expected = normYmd(enriched.expectedConfirmYmd);
      if (!expected || today < expected) continue;
      return true;
    }
  }
  return false;
}

export { listPendingBuyTrades, listPendingRedeemTrades, productKey, formatMoney2 as fmtMoney2, formatShares2 as fmtShares2 };
